package dev.lingvo.shared.screenshots.doc

import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive

/**
 * Writes [element] as compact JSON that is byte-identical on the JVM and in the browser, keeping the element's key
 * order. Internal on purpose: it is canonical only for elements the model's serializer produced (keys in
 * declaration order, maps sorted by [DesignCodec.sortedMaps]). Anything stored goes through [DesignCodec.encode].
 *
 * kotlinx writes a `Double` with the platform's `toString()`: `1290.0` and `1.0E-5` on the JVM, `1290` and
 * `0.00001` in JavaScript. This writer re-spells every number one way: the shortest round-trip digits (both
 * platforms produce those), laid out as ECMAScript's `Number.prototype.toString` does. An integral double prints as
 * an integer (`1290`); `-0` and subnormals print as `0`, which is why the validator refuses both (`-0.0` and
 * non-zero magnitudes below [DesignLimits.MIN_MAGNITUDE]), so `decode(encode(d)) == d` for every valid design. This
 * relies on the JVM's `Double.toString` giving the shortest digits, which it does from JDK 19 (the build runs on
 * 21). Strings escape `"`, `\`, controls and lone surrogates. Iterative, so a deep element cannot overflow the stack.
 *
 * @throws IllegalArgumentException for a non-string primitive that is not a JSON number, boolean or null.
 */
internal fun normalizedJson(element: JsonElement): String {
    val out = StringBuilder()
    val stack = ArrayList<CanonicalFrame>()
    writeCanonical(element, out, stack)
    while (stack.isNotEmpty()) {
        val frame = stack[stack.size - 1]
        if (!frame.hasNext()) {
            out.append(frame.close)
            stack.removeAt(stack.size - 1)
            continue
        }
        if (frame.started) out.append(',') else frame.started = true
        val next = frame.next(out)
        writeCanonical(next, out, stack)
    }
    return out.toString()
}

/** Re-spells a JSON number one way on every platform (see [normalizedJson]). */
internal fun normalizedNumber(literal: String): String {
    val match = NUMBER_REGEX.matchEntire(literal) ?: throw IllegalArgumentException("Not a JSON number: $literal")
    val negative = match.groupValues[1] == "-"
    val integerPart = match.groupValues[2]
    val fraction = match.groupValues[3]
    val exponentText = match.groupValues[4]
    val exponent = if (exponentText.isEmpty()) 0 else {
        exponentText.toIntOrNull() ?: throw IllegalArgumentException("Exponent out of range: $literal")
    }
    var digits = (integerPart + fraction).trimStart('0')
    if (digits.isEmpty()) return "0"
    // Subnormals are where the JVM keeps two digits (4.9E-324) and JavaScript one (5e-324). No design value is
    // within 300 orders of magnitude of them, so they are written as zero rather than spelled two ways.
    if (kotlin.math.abs(literal.toDouble()) < MIN_NORMAL) return "0"
    var exp10 = exponent - fraction.length
    val trimmed = digits.trimEnd('0')
    exp10 += digits.length - trimmed.length
    digits = trimmed
    val count = digits.length
    // Position of the decimal point counted from the left of the digits, as ECMAScript's "n".
    val point = count + exp10
    val body = if (point in -5..21) {
        when {
            point >= count -> digits + "0".repeat(point - count)
            point > 0 -> digits.substring(0, point) + "." + digits.substring(point)
            else -> "0." + "0".repeat(-point) + digits
        }
    } else {
        val power = point - 1
        val mantissa = if (count == 1) digits else digits[0] + "." + digits.substring(1)
        mantissa + "e" + (if (power >= 0) "+" else "-") + kotlin.math.abs(power)
    }
    return if (negative) "-$body" else body
}

private const val MIN_NORMAL = 2.2250738585072014E-308

private val NUMBER_REGEX =Regex("^(-?)(0|[1-9][0-9]*)(?:\\.([0-9]+))?(?:[eE]([+-]?[0-9]+))?$")

private class CanonicalFrame(
    private val objectEntries: Iterator<Map.Entry<String, JsonElement>>?,
    private val arrayItems: Iterator<JsonElement>?,
    val close: Char,
) {
    var started = false

    fun hasNext(): Boolean = objectEntries?.hasNext() ?: arrayItems!!.hasNext()

    fun next(out: StringBuilder): JsonElement {
        if (objectEntries != null) {
            val entry = objectEntries.next()
            writeString(entry.key, out)
            out.append(':')
            return entry.value
        }
        return arrayItems!!.next()
    }
}

private fun writeCanonical(element: JsonElement, out: StringBuilder, stack: MutableList<CanonicalFrame>) {
    when (element) {
        is JsonObject -> {
            out.append('{')
            stack.add(CanonicalFrame(element.entries.iterator(), null, '}'))
        }
        is JsonArray -> {
            out.append('[')
            stack.add(CanonicalFrame(null, element.iterator(), ']'))
        }
        is JsonNull -> out.append("null")
        is JsonPrimitive -> when {
            element.isString -> writeString(element.content, out)
            element.content == "true" || element.content == "false" -> out.append(element.content)
            else -> out.append(normalizedNumber(element.content))
        }
    }
}

private fun writeString(value: String, out: StringBuilder) {
    out.append('"')
    var index = 0
    while (index < value.length) {
        val char = value[index]
        when {
            char == '"' -> out.append("\\\"")
            char == '\\' -> out.append("\\\\")
            char == '\n' -> out.append("\\n")
            char == '\r' -> out.append("\\r")
            char == '\t' -> out.append("\\t")
            char == '\b' -> out.append("\\b")
            char == '\u000C' -> out.append("\\f")
            char.code < 0x20 -> appendUnicodeEscape(char, out)
            char.isHighSurrogate() && index + 1 < value.length && value[index + 1].isLowSurrogate() -> {
                out.append(char).append(value[index + 1])
                index++
            }
            // A lone surrogate is not valid UTF-8; escaping keeps the stored bytes decodable.
            char.isSurrogate() -> appendUnicodeEscape(char, out)
            else -> out.append(char)
        }
        index++
    }
    out.append('"')
}

private fun appendUnicodeEscape(char: Char, out: StringBuilder) {
    val hex = char.code.toString(16)
    out.append("\\u").append("0".repeat(4 - hex.length)).append(hex)
}
